import React, { useCallback, useEffect, useState } from 'react';
import Dropzone, { Accept } from 'react-dropzone';
import ModalDialog from '../../components/modal/ModalDialog';
import { Button, ButtonSize, ButtonTheme } from '../Button';

import './FileUploadModal.scss';
import { useDrop } from './useDrop';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly title: string;
    readonly description: string;
    readonly accept: Accept;
    readonly maxFileSizeInMb?: number;
    readonly show: boolean;
    readonly onUpload: (file: File, callback: (percent: number) => void) => void;
    readonly onAbort?: () => void;
    readonly onClose?: () => void;
    readonly allowedFileFormatLabel: string;
    readonly showFileRejectedError: (message: string) => void;
};

export const FileUploadModal: React.FC<Props> = ({
    title = 'Upload',
    accept = {},
    maxFileSizeInMb = 100,
    show = false,
    onUpload = () => {},
    onAbort,
    onClose,
    description,
    allowedFileFormatLabel,
    showFileRejectedError = () => {}
}) => {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [percent, setPercent] = useState(0);
    const [filename, setFilename] = useState('');

    const allowedMIMETypes = Object.keys(accept);
    const allowedFileExtensions = Object.values(accept).flat();

    const reset = useCallback(() => {
        setUploading(false);
        setPercent(0);
        setFilename('');
    }, []);

    const close = useCallback(() => {
        if (uploading) {
            if (onAbort) {
                onAbort();
            }
        } else {
            if (onClose) {
                onClose();
            }
        }
        reset();
    }, [uploading, onAbort, onClose, reset]);

    const uploadFile = useCallback(
        file => {
            setUploading(true);
            setFilename(file.name);
            onUpload(file, setPercent);
        },
        [onUpload]
    );

    const { onDrop } = useDrop({
        onDropFile: uploadFile,
        onError: showFileRejectedError,
        allowedFileFormatLabel,
        allowedMIMETypes,
        allowedFileExtensions,
        maxFileSizeInMb
    });

    useEffect(() => {
        if (show) {
            reset();
        }
    }, [show, reset]);

    return (
        <ModalDialog
            width={750}
            overwriteWidth={true}
            titleHeaderGrade={2}
            show={show}
            title={title}
            onCloseButtonClick={close}
        >
            <div className="file-upload-popup">
                <p>{description}</p>
                {uploading ? (
                    <div>
                        <div className="progress-bar-container">
                            <div className="progress-bar" style={{ width: `${percent}%` }} />
                        </div>
                        <p>Uploading {filename}...</p>
                    </div>
                ) : (
                    <div className="file-upload">
                        <Dropzone
                            maxSize={maxFileSizeInMb * 1024 * 1024}
                            multiple={false}
                            accept={accept}
                            onDrop={onDrop}
                            useFsAccessApi={false}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <div className="dropzone" {...getRootProps()} onClick={() => {}}>
                                    <input {...getInputProps()} />
                                    <Button
                                        text={t('yourLocalContent.yourTitles.dropFile')}
                                        icon="file_upload"
                                        size={ButtonSize.LARGE}
                                        theme={ButtonTheme.NEUTRAL}
                                        onClick={event => {
                                            const handler = getRootProps().onClick;

                                            if (handler) {
                                                handler(event);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </Dropzone>
                    </div>
                )}
                <div className="buttons">
                    <Button text={t('cancel')} onClick={close} size={ButtonSize.LARGE} />
                </div>
            </div>
        </ModalDialog>
    );
};
